import { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Checkbox,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { initialPath } from "~misc/paths";
import warningImg from "~assets/images/warningImg.png";
import { ClearMM, IconWarning } from "~misc/icons";
import Select from "~components/Form/components/Select";
import { isPastMonth, isPastNegotiationDate, negotiationDateToSortKey } from "../../helpers/pastDate";
import "~pages/PricesTableV2/components/Tabela/style.scss";
import { formatDateByPattern, formatMonthYear, isValidBRDate, isValidMonthYear } from "~pages/FunnelV2/helpers/funnelHelpers";
import { db } from "~db/db";
import ModalAction from "~components/Modal/ModalAction";

const HeaderCliente = ({ cliente, viewBy }) => {
  const [clienteNome, setClienteNome] = useState(cliente.nome);
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.stopPropagation();
    navigate(`${initialPath}/funil-de-obras/${cliente.clienteId}/acompanhamento-mapeamento`);
  };

  useEffect(() => {
    if(viewBy === "razaoSocial") {
      setClienteNome(cliente.nome);
    }else{
      setClienteNome(cliente.nomeObra);
    }
  }, [viewBy]);

  return (
    <span
      role="link"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleClick(e);
      }}
      className="cursor-pointer hover:underline"
    >
      <span className={cliente.isProspect ? " font-bold" : "font-bold"}>
        {cliente.isProspect ? cliente.codigoProspect : cliente.codigo}
      </span>
      {" - "}
      <span>{clienteNome}</span>
    </span>
  );
};

HeaderCliente.propTypes = {
  cliente: PropTypes.object.isRequired,
};

const NegociacaoCell = ({ rowId, clienteId, dataNegociacao, funilObraStatusId, hasWarning, updateData, setDirty, emNegociacaoStatusId }) => {
  const [originalValue, setOriginalValue] = useState("");
  const [value, setValue] = useState(dataNegociacao ?? originalValue);
  const isEdited = value !== originalValue;

  const handleChange = useCallback(
    (e) => {
      const nextData = e.target.value;

      if(isValidBRDate(`01/${nextData.split('-').reverse().join('/')}`)) {
        updateData({clienteId, id: rowId, data: { dataNegociacao: (`${nextData}-01`)?.toString() }});
      }
      setValue(nextData);
    },
    [originalValue, rowId]
  );

  const handleSetDirty = (invalid) => {
    setDirty(prev => ({
      ...prev,
      [`id-${rowId}`]: invalid !== undefined ? invalid : isEdited
    }))
  }

  const handleReset = async () => {
    setValue(originalValue);

    const toUpdate = await getFunnelByItem();
    if(toUpdate) {
      updateData({clienteId, id: rowId , data: { dataNegociacao: toUpdate.dataNegociacao.substring(0, 10) }});
    }
  };

  const getFunnelByItem = async () => {
    if(!isNaN(rowId) || rowId.length == 10) {
      return await db.funilObra.get(rowId) ?? await db.funilObraDashboard.get(rowId);
    }

    return null;
  }

  const editedStyle = isEdited
    ? { border: "1px solid #034EA2", background: "#E2F0FF", color: "#034EA2" }
    : { border: "1px solid #ccc", background: "#fff" };

  useEffect(() => {
    const [, month, year] = dataNegociacao.split('/');
    setValue(`${year}-${month}`);
  }, [dataNegociacao]);

  useEffect(() => {
    (async() => {
      const funnel = await db.funilObra.get(rowId) ?? await db.funilObraDashboard.get(rowId);
      if(funnel) {
        const date = new Date(funnel.dataNegociacao);
        setOriginalValue(formatDateByPattern(date, "yyyy-mm"));
      }
    })()
  }, [rowId]);

  useEffect(() => {
    handleSetDirty();
  }, [originalValue]); 

  //A IDEIA É CHECAR SE O VALOR É UMA DATA VÁLIDA OU NÃO.
  //DARIA PRA APLICAR TAMBÉM UMA CHECAGEM DE MAIOR OU MENOR DO QUE HOJE MAS
  //PRECISAMOS AGUARDAR AS REGRAS.
  useEffect(() => {
    if (!isValidMonthYear(value.split('-').reverse().join('/'))) {
      handleSetDirty('invalid-date');
      return;
    }

    if (isPastMonth(value)) {
      handleSetDirty('invalid-date');
    } else {
      handleSetDirty();
    }
  }, [value]);

  return (
    <div className="flex items-center gap-2">
      <input
        type="month"
        // inputMode="numeric"
        value={value}
        onChange={handleChange}
        placeholder="MM/YYYY"
        // maxLength={10}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          // width: 100,
          padding: "8px",
          borderRadius: 4,
          fontWeight: 700,
          textAlign: "center",
          outline: "none",
          ...editedStyle,
        }}
      />

      {
        hasWarning || isPastNegotiationDate(value, funilObraStatusId, emNegociacaoStatusId) && ( <img src={warningImg} className="w-5 h-5" alt="alerta" /> )
      }

      {isEdited && (
        <button
          type="button"
          onClick={handleReset}
          aria-label="Limpar edição"
          className="w-7 h-7 rounded-full bg-[#034EA2] flex items-center justify-center hover:opacity-90 transition"
        >
          <span className="scale-50">
            <ClearMM />
          </span>
        </button>
      )}
    </div>
  );
};

const VigenciaTable = ({ produtos, clienteId, isProspect, selected, onToggle, updateData, setDirty, emNegociacaoStatusId }) => {
  const volumeLabel = isProspect ? "Volume" : "Volume Negociado";

  return (
    <div className="table-container">
      <Table className="table">
        <colgroup>
          <col style={{ width: 40 }} />
          <col style={{ width: 320 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: 200 }} />
        </colgroup>

        <Thead>
          <Tr>
            <Th />
            <Th>Grupo Produto</Th>
            <Th>{volumeLabel}</Th>
            <Th>Próx. Negociação</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>

        <Tbody>
          {(produtos || []).map((row) => (
            <Tr key={row.id}>
              <Td className="no-border checkbox-column ">
                <Checkbox
                  isChecked={!!selected[row.id]}
                  onChange={() => onToggle(row.id)}
                  colorScheme="blue"
                />
              </Td>
              <Td>{row.grupoProduto}</Td>
              <Td>{row.volume}</Td>
              <Td className="no-border">
                <NegociacaoCell
                  clienteId={clienteId}
                  key={`${row.id}-${row.dataNegociacao}`}
                  rowId={row.id}
                  dataNegociacao={row.dataNegociacao}
                  funilObraStatusId={row.funilObraStatusId}
                  hasWarning={row.hasWarning}
                  updateData={updateData}
                  setDirty={setDirty}
                  emNegociacaoStatusId={emNegociacaoStatusId}
                />
              </Td>
              <Td className="no-border">{row.status}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

VigenciaTable.propTypes = {
  vigencia: PropTypes.object.isRequired,
  isProspect: PropTypes.bool,
  selected: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  updateData: PropTypes.func,
  setDirty: PropTypes.func,
  emNegociacaoStatusId: PropTypes.number
};

const prospectColor = "#9ACA3C";

const ClientAccordionItem = ({ item, selected, onToggle, updateData, setDirty, emNegociacaoStatusId, viewBy }) => {
  const prospectSx = item.isProspect
    ? {
        ".chakra-accordion__button": { backgroundColor: `${prospectColor} !important` },
        ".chakra-collapse": { borderColor: `${prospectColor} !important` },
      }
    : undefined;

  return (
    <Accordion className="accordion" allowToggle defaultIndex={[0]}>
      <AccordionItem sx={prospectSx}>
        <AccordionButton>
          <HeaderCliente cliente={item} viewBy={viewBy} />
        </AccordionButton>
        <AccordionPanel>
          <VigenciaTable
            key={item.id}
            produtos={item.produtos}
            clienteId={item.clienteId}
            isProspect={item.isProspect}
            selected={selected}
            onToggle={onToggle}
            updateData={updateData}
            setDirty={setDirty}
            emNegociacaoStatusId={emNegociacaoStatusId}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

ClientAccordionItem.propTypes = {
  item: PropTypes.object.isRequired,
  selected: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  updateData: PropTypes.func,
  setDirty: PropTypes.func,
  emNegociacaoStatusId: PropTypes.number
};

const sortOptions = [
  { value: "todas", label: "Todas" },
  { value: "maisRecentes", label: "Mais Recentes" },
  { value: "maisAntigos", label: "Mais Antigos" },
];

const viewOptions = [
  { value: "nomeObra", label: "Nome de Obra" },
  { value: "razaoSocial", label: "Razão Social" },
];

const clientHasPast = (item, emNegociacaoStatusId) =>
  (item.produtos || []).some(
    (p) => p.hasWarning || isPastNegotiationDate(p.dataNegociacaoOriginal, p.funilObraStatusId, emNegociacaoStatusId)
  )

const clientOldestKey = (item) => {
  const keys = (item.produtos || []).map((p) => negotiationDateToSortKey(p.dataNegociacao));
  return keys.length ? Math.min(...keys) : Number.POSITIVE_INFINITY;
};

const NegotiationsAccordion = ({ items, selected = {}, onToggle, onSelectAll, setDirty, updateData, emNegociacaoStatusId }) => {
  const [sortBy, setSortBy] = useState("maisAntigos");
  const [viewBy, setViewBy] = useState("razaoSocial");
  const [pastDateModalOpen, setPastDateModalOpen] = useState(false);

  const allIds = (items || []).flatMap((item) =>
    (item.produtos || []).map((p) => p.id)
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastIds = (items || []).flatMap(item =>
    (item.produtos || [])
      .filter(produto => isPastNegotiationDate(produto.dataNegociacaoOriginal, produto.funilObraStatusId, emNegociacaoStatusId))
      .map(produto => produto.id)
  );

  const allSelected = pastIds.length > 0 && pastIds.every(id => !!selected[id]);

  const hasPast = useMemo(
    () =>
      (items || []).some(item =>
        clientHasPast(item, emNegociacaoStatusId)
      ),
    [items, emNegociacaoStatusId]
  );

  const sortedItems = useMemo(() => {
    const sortProdutos = (produtos) => {
      const arr = [...(produtos || [])];
      if (sortBy === "maisAntigos") {
        arr.sort(
          (a, b) =>
            negotiationDateToSortKey(a.dataNegociacao) -
            negotiationDateToSortKey(b.dataNegociacao)
        );
      } else if (sortBy === "maisRecentes") {
        arr.sort(
          (a, b) =>
            negotiationDateToSortKey(b.dataNegociacao) -
            negotiationDateToSortKey(a.dataNegociacao)
        );
      }
      return arr;
    };

    const list = (items || []).map((client) => ({
      ...client,
      produtos: sortProdutos(client.produtos)
    }));

    if (sortBy === "maisAntigos") {
      list.sort((a, b) => clientOldestKey(a) - clientOldestKey(b));
    } else if (sortBy === "maisRecentes") {
      list.sort((a, b) => clientOldestKey(b) - clientOldestKey(a));
    }
    return list;
  }, [items, sortBy]);

  return (
    <main className="prices-table-table">
      <div className="container content">
        <div className="flex justify-between  items-center mb-2 gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-primaria">Selecionar negociações no passado</span>
            <Checkbox
              isChecked={allSelected}
              borderColor="gray.400"
              onChange={() => {
                if (pastIds.length === 0) setPastDateModalOpen(true);
                onSelectAll?.(pastIds, !allSelected);
              }
              }
              colorScheme="blue"
            />
          </div>
          <div className="flex gap-4">
            <div className="min-w-[260px]">
              <Select
                label="Visualização por:"
                name="viewBy"
                options={viewOptions}
                value={viewOptions.find((o) => o.value === viewBy) ?? viewOptions[0]}
                onChange={(opt) => setViewBy(opt?.value ?? "razaoSocial")}
                placeholder="Selecione"
                spacingControl={true}
              />
            </div>
            <div className="min-w-[260px]">
              <Select
                label="Ordenar por:"
                name="sortBy"
                options={sortOptions}
                value={sortOptions.find((o) => o.value === sortBy) ?? sortOptions[0]}
                onChange={(opt) => setSortBy(opt?.value ?? "maisAntigos")}
                placeholder="Selecione"
                spacingControl={true}
              />
            </div>
          </div>
        </div>
        {hasPast && (
          <div className="relative mb-4 ml-1">
            <div
              className="absolute left-0 top-3 bottom-3 w-1.5 bg-[#EE4F2C] rounded-full"
              style={{ zIndex: 0 }}
            />
            <div
              className="relative flex items-center justify-center gap-8 bg-[#FBEAEA] rounded-xl px-8 py-4 ml-1"
              style={{ zIndex: 1 }}
            >
              <IconWarning fill="#EE4F2C" width="40" height="36" />
              <p className="text-sm font-bold text-[#01054C]">
                Existem Obras com Negociação no Passado!
              </p>
            </div>
          </div>
        )}
        {sortedItems.map((item) => (
          <ClientAccordionItem
            key={item.clienteId}
            item={item}
            viewBy={viewBy}
            selected={selected}
            onToggle={onToggle}
            updateData={updateData}
            setDirty={setDirty}
            emNegociacaoStatusId={emNegociacaoStatusId}
          />
        ))}
      </div>

      <ModalAction
        isOpen={pastDateModalOpen}
        onClose={() => setPastDateModalOpen(false)}
        status="alert"
        title="Não existem negociações no passado"
        limite
        buttonTwo="OK"
        linkTwo={() => setPastDateModalOpen(false)}
        buttonTwoClassName="px-6 py-3 rounded bg-secundaria text-primaria font-bold border-2 border-secundaria hover:bg-white hover:text-secundaria transition duration-300 ease-in-out"
      />
    </main>
  );
};

NegotiationsAccordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      clienteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      codigo: PropTypes.string.isRequired,
      codigoProspect: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nome: PropTypes.string.isRequired,
      isProspect: PropTypes.bool,
      produtos: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          grupoProduto: PropTypes.string.isRequired,
          volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          dataNegociacao: PropTypes.string.isRequired,
          status: PropTypes.string.isRequired,
          hasWarning: PropTypes.bool,
          checked: PropTypes.bool,
        })
      ).isRequired,
    })
  ).isRequired,
  selected: PropTypes.object,
  onToggle: PropTypes.func,
  onSelectAll: PropTypes.func,
  edits: PropTypes.object,
  onRowEdit: PropTypes.func,
  onRowReset: PropTypes.func,
  emNegociacaoStatusId: PropTypes.number,
};

export default NegotiationsAccordion;
